import React, { useState } from "react";
import { Link as LinkIcon, Copy, GoogleLogo, MetaLogo, Info } from "@phosphor-icons/react";
import { toast } from "sonner";

const UTM_FIELDS = [
  ["utm_source", "Source", "meta / google / whatsapp"],
  ["utm_medium", "Medium", "cpc / paid_social / organic"],
  ["utm_campaign", "Campaign", "diwali_2026_ht"],
  ["utm_content", "Content / creative", "Ad variant"],
  ["utm_term", "Term / keyword", "Paid keyword"],
];

const AD_FIELDS = [
  ["ad_id", "Ad ID"],
  ["adset_id", "Adset / Ad group ID"],
];

const PAGE_FIELDS = [
  ["landing_page", "Landing page"],
  ["referrer", "Referrer"],
];

/**
 * "Attribution" section for LeadDetail. Renders every non-empty UTM / click-ID
 * / page-context field grouped visually. Silently hides itself when the lead
 * carries zero attribution (walk-in / direct-call leads).
 */
export default function AttributionSection({ lead }) {
  const hasAny =
    UTM_FIELDS.some(([k]) => lead[k]) ||
    AD_FIELDS.some(([k]) => lead[k]) ||
    PAGE_FIELDS.some(([k]) => lead[k]) ||
    lead.gclid || lead.fbclid || lead.campaign_id;

  const [showRaw, setShowRaw] = useState(false);

  if (!hasAny) {
    return (
      <div className="border border-border bg-white" data-testid="attribution-section">
        <div className="px-5 py-3 border-b border-border flex items-center gap-2">
          <LinkIcon size={14} />
          <h3 className="font-display text-lg font-semibold tracking-tight">Attribution</h3>
        </div>
        <div className="p-5 text-sm text-muted-foreground italic">
          No UTM tags, click IDs, or campaign attribution captured for this lead.
        </div>
      </div>
    );
  }

  const copyJson = async () => {
    const payload = {};
    ["campaign_id", "campaign_name",
     ...UTM_FIELDS.map(f => f[0]), ...AD_FIELDS.map(f => f[0]),
     ...PAGE_FIELDS.map(f => f[0]), "gclid", "fbclid"].forEach((k) => {
      if (lead[k]) payload[k] = lead[k];
    });
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      toast.success("Attribution JSON copied");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="border border-border bg-white" data-testid="attribution-section">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LinkIcon size={14} />
          <h3 className="font-display text-lg font-semibold tracking-tight">Attribution</h3>
        </div>
        <div className="flex gap-1">
          <button onClick={() => setShowRaw((v) => !v)}
            className="text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 border border-border hover:bg-secondary">
            {showRaw ? "Grouped" : "Raw JSON"}
          </button>
          <button onClick={copyJson} data-testid="attribution-copy-json"
            className="text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-1 border border-border hover:bg-secondary inline-flex items-center gap-1">
            <Copy size={11} /> Copy
          </button>
        </div>
      </div>
      {showRaw ? (
        <pre className="p-5 text-[11px] font-mono whitespace-pre-wrap break-all bg-secondary/20 overflow-x-auto">
{JSON.stringify(
  ["campaign_id", "campaign_name", ...UTM_FIELDS.map(f => f[0]),
   ...AD_FIELDS.map(f => f[0]), ...PAGE_FIELDS.map(f => f[0]), "gclid", "fbclid"]
    .reduce((a, k) => (lead[k] ? { ...a, [k]: lead[k] } : a), {}),
  null, 2
)}
        </pre>
      ) : (
        <div className="p-5 space-y-5">
          {(lead.campaign_id || lead.campaign_name) && (
            <Group title="Campaign">
              {lead.campaign_id && <Field label="Campaign ID">
                <span className="font-mono text-[11px]">{lead.campaign_id}</span>
              </Field>}
              {lead.campaign_name && <Field label="Campaign name">
                <span className="inline-block px-2 py-0.5 bg-primary/10 border border-primary/40 text-[11px] font-semibold text-primary">
                  {lead.campaign_name}
                </span>
              </Field>}
            </Group>
          )}
          {UTM_FIELDS.some(([k]) => lead[k]) && (
            <Group title="UTM tags">
              {UTM_FIELDS.map(([k, label, hint]) => lead[k] && (
                <Field key={k} label={label} hint={hint}>
                  <span className="font-mono text-[12px]">{lead[k]}</span>
                  {lead.attribution_inferred && (k === "utm_source" || k === "utm_medium") && (
                    <span
                      title={
                        lead.attribution_inferred === "gclid"
                          ? "Inferred from gclid — Google Ads auto-tagging sends only gclid, not utm_*"
                          : "Inferred from fbclid — Meta doesn't append utm_* automatically"
                      }
                      className="ml-2 inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-[0.15em] font-bold border border-amber-500 text-amber-700 bg-amber-500/10"
                      data-testid="attribution-inferred-badge"
                    >
                      Inferred
                    </span>
                  )}
                </Field>
              ))}
            </Group>
          )}
          {(lead.gclid || lead.fbclid) && (
            <Group title="Click IDs">
              {lead.gclid && <Field label="gclid" icon={GoogleLogo} tone="google">
                <span className="font-mono text-[10px] break-all">{lead.gclid}</span>
              </Field>}
              {lead.fbclid && <Field label="fbclid" icon={MetaLogo} tone="meta">
                <span className="font-mono text-[10px] break-all">{lead.fbclid}</span>
              </Field>}
            </Group>
          )}
          {AD_FIELDS.some(([k]) => lead[k]) && (
            <Group title="Ad targeting">
              {AD_FIELDS.map(([k, label]) => lead[k] && (
                <Field key={k} label={label}>
                  <span className="font-mono text-[11px]">{lead[k]}</span>
                </Field>
              ))}
            </Group>
          )}
          {PAGE_FIELDS.some(([k]) => lead[k]) && (
            <Group title="Page context">
              {PAGE_FIELDS.map(([k, label]) => lead[k] && (
                <Field key={k} label={label}>
                  <a href={lead[k]} target="_blank" rel="noreferrer noopener"
                    className="text-primary hover:underline break-all text-[11px]">
                    {lead[k]}
                  </a>
                </Field>
              ))}
            </Group>
          )}
        </div>
      )}
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2 flex items-center gap-1">
        {title}
      </div>
      <dl className="space-y-1.5">{children}</dl>
    </div>
  );
}

function Field({ label, hint, icon: Icon, tone, children }) {
  const toneColor = tone === "google" ? "text-[#4285F4]" : tone === "meta" ? "text-[#1877F2]" : "";
  return (
    <div className="grid grid-cols-[140px_1fr] items-start gap-3 text-sm">
      <dt className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground pt-1 flex items-center gap-1.5">
        {Icon && <Icon size={12} weight="fill" className={toneColor} />}
        <span>{label}</span>
        {hint && <span title={hint}><Info size={10} className="text-muted-foreground/60" /></span>}
      </dt>
      <dd className="break-all">{children}</dd>
    </div>
  );
}
